import React, { useEffect, useState } from 'react';
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import validate from "utils/validate";
import DynamicHeadTag from "../common/DynamicHeadTag";
import Maybe from "../common/Maybe";
import { ButtonLoader, FileUploadForm } from "../common/form";
import { ReactComponent as IconCashPayment } from "assets/images/icons/CashPayment.svg";
import { ReactComponent as IconReport } from "assets/images/icons/Report.svg";
import { ReactComponent as IconFileUpload } from "assets/images/icons/upload-cloud.svg";
import { ReactComponent as IconDoc } from "assets/images/icons/doc.svg";
import useBackButton from "hooks/useBackButton";
import * as settingsAction from "store/entities/settingsInfo/action";
import * as accountActions from "store/auth/account/action";
import useMediaService from "hooks/useMediaService";
import useLoginInfo from "hooks/useLoginInfo";
import { LottieAnimation } from '../common/LottieAnimation';
import tickLottieData from 'assets/lottie/tick.json';
import crossLottieData from 'assets/lottie/cross.json';


const OnDemandCompliance = () => {
    const dispatch = useDispatch();
    const {goBack} = useBackButton();
    const {userInfo} = useLoginInfo();
    const { uploadFile, uploadLoading } = useMediaService();
    const { bankStatement, bankStatementLoading } = useSelector((state) => state.entities.settingsInfo);

    const [tab, setTab] = useState(""); // welcome, readyToSubmit, submitted, rejected
    const [form, setForm] = useState({
        bankStatementDocument: "",
        documentName: "bank-statement",
        companyName: userInfo?.company?.companyName
    })

    const isFormValidated = !form?.bankStatementFile;

    const handleFileChange = async (file) => {
        if (file !== null && file !== undefined) {
            const response = await uploadFile(file, form?.documentName, form?.companyName);
            if (response?.status === "success") {
                setForm({
                    ...form,
                    bankStatementDocument: file,
                    bankStatementFile: response?.url
                });
                toast.success("File uploaded successfully");
            } else {
                toast.error(response?.message);
            }
        } else {
            toast.error("Please select a file to upload");
        }
    };

    const submitForm = async (e) => {
        e.preventDefault();
        if (form?.bankStatementDocument) {
            const params = { bankStatementFile: form?.bankStatementFile };
            dispatch(settingsAction.updateBankStatement(params));
        } else {
            toast.error("Please select a file to upload");
        }
    }

    useEffect(() => {
        let activeTab = "";
        const {odpStatus} = userInfo?.employer;
        if (odpStatus === undefined){
            activeTab = "welcome";
        }
        else if (odpStatus === "pending"){
            activeTab = "submitted";
        }
        else if (odpStatus === "rejected"){
            activeTab = "rejected";
        }
        setTab(activeTab);
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        if (bankStatement?.status === "success"){
            setTab("submitted");
            const params = {
                ...userInfo,
                employer: {
                    ...userInfo.employer,
                    odpStatus: "pending",
                }
            }
            dispatch(accountActions.updateLoginProfile(params));
        }
        // eslint-disable-next-line
    }, [bankStatement])
    
    
    return (
        <div>
            <DynamicHeadTag
                headerText="OnDemand Onboarding"
            />
            
            {tab === "welcome" &&
                <Welcome setTab={setTab} />
            }
            
            {tab === "readyToSubmit" &&
                <div className="p-6 mx-auto max-w-xl bg-white mt-7 rounded-2xl overflow-hidden">
                    <form onSubmit={submitForm}>
                        <div>
                            <div className="font-semibold text-xl">
                                On-Demand Pay Qualification
                            </div>
                            <div className="text-[#4A5564] text-sm">
                                We require your company’s bank statement to verify your eligibility
                            </div>
                        </div>
                        <div className="mt-6">
                            <div className="font-medium text-sm text-[#1A202C]">
                                Company’s Bank Statement
                            </div>
                            <div className="mt-2">
                                <FileUploadForm
                                    data={form.bankStatementDocument}
                                    onchange={handleFileChange}
                                    filetype={["pdf"]}
                                    maxFileSize={10}
                                    filename="Bank statement"
                                    uploadUi={<>
                                        <div className="h-56 bg-white border border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer">
                                            <div className="w-8 h-8 flex">
                                                <IconFileUpload className="m-auto w-full h-full" />
                                            </div>
                                            <div className="mt-2">
                                                <span className="text-[#718096]">
                                                    Drag and drop file or&nbsp;
                                                </span>
                                                <span className="text-ep-primary border-b border-ep-primary">
                                                    browse
                                                </span>
                                            </div>
                                        </div>
                                    </>}
                                />
                                
                            </div>
                            <div className="mt-8">
                                <ButtonLoader type="submit" loading={uploadLoading || bankStatementLoading} disabled={isFormValidated} className="btn btn-block btn-lg btn-ep-primary">
                                    Submit
                                </ButtonLoader>
                            </div>
                        </div>
                    </form>
                </div>
            }
            
            {tab === "submitted" &&
                <div className="mx-auto p-8 mt-10 max-w-xl text-center rounded-2xl bg-ep-white">
                    <div className="h-24">
                        <LottieAnimation animationData={tickLottieData} />
                    </div>
                    <div className="mt-8 font-semibold text-2xl">
                        Request Submitted
                    </div>
                    <div className="mt-4 text-[#4A5564] font-medium">
                        Your On-Demand Pay request is currently under review. 
                        <br /><br />
                        Please allow 1-3 business days for processing. You will be notified once your request has been approved.
                    </div>
                    <div className="mt-8">
                        <button type="button" onClick={goBack} className="btn btn-block btn-lg btn-ep-primary h-12 text-base">
                            Close
                        </button>
                    </div>
                </div>
            }
            
            {tab === "rejected" &&
                <div className="mx-auto p-8 mt-10 max-w-xl rounded-2xl bg-ep-white">
                    <div className="h-24">
                        <LottieAnimation animationData={crossLottieData} />
                    </div>
                    <div className="text-center">
                        <div className="mt-8 font-semibold text-2xl">
                            Request Denied
                        </div>
                        <div className="mt-4 text-[#4A5564] font-medium">
                            After analysing your bank statement, we regret to inform you that your On-Demand Pay request has been denied due to ineligibility.
                            <br /><br />
                            If you believe this is an error, please review your submitted bank statement and try again. For further assistance, please contact our support team at &nbsp;
                            <a href="mailto:support@earnipay.com" rel="noreferrer" target="_blank" className="text-ep-primary underline">
                                support@earnipay.com
                            </a>.
                        </div>
                    </div>
                    <form onSubmit={submitForm}>
                        <div className="mt-8">
                            <div className="font-medium text-base text-[#1A202C]">
                                Company’s Bank Statement
                            </div>
                            
                            <div className="mt-2">
                                <FileUploadForm
                                    data={form.bankStatementDocument}
                                    onchange={handleFileChange}
                                    filetype={["pdf"]}
                                    maxFileSize={10}
                                    filename="Bank statement"
                                    uploadUi={<>
                                        <div className="h-36 bg-white border border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer">
                                            <div className="w-8 h-8 flex">
                                                <IconFileUpload className="m-auto w-full h-full" />
                                            </div>
                                            <div className="mt-2">
                                                <span className="text-[#718096]">
                                                    Drag and drop file or&nbsp;
                                                </span>
                                                <span className="text-ep-primary border-b border-ep-primary">
                                                    browse
                                                </span>
                                            </div>
                                        </div>
                                    </>}
                                />
                            </div>
                            
                            <div className="flex items-center ellipsis-2-lines overflow-hidden">
                                <Link to={{pathname: userInfo?.company?.bankStatementFile}} target="_blank" rel="noreferrer" className="w-12 h-12 flex">
                                    <IconDoc className="h-full object-contain relative -left-1" />
                                </Link>
                                <div className="font-medium">
                                    <Link to={{pathname: userInfo?.company?.bankStatementFile}} target="_blank" rel="noreferrer" className="hover:underline">
                                        document.pdf
                                        &nbsp;
                                        {/* <span className="text-gray-500">(236.1KB)</span> */}
                                    </Link>
                                </div>
                            </div>
                            
                            <div className="mt-8">
                                <ButtonLoader type="submit" loading={uploadLoading || bankStatementLoading} disabled={isFormValidated} className="btn btn-block btn-lg btn-ep-primary h-12 text-base">
                                    Submit
                                </ButtonLoader>
                            </div>
                        </div>
                    </form>
                </div>
            }
            
        </div>
    )
}


const Welcome = (props) => {
    const {setTab} = props;
    const [step, setStep] = useState(1);
    
    return (
        <div>
            <Maybe condition={step === 1}>
                <WelcomeScreen
                    step={step}
                    image={<IconCashPayment className="w-full h-full object-contain" />}
                    title="Biggest Source of Workplace Stress Solved"
                    description="Reduce distractions caused by financial stress on your team at no cost to you and without disrupting your payroll system"
                    submitText="Next"
                    submitAction={() => setStep(2)}
                />
            </Maybe>
            
            <Maybe condition={step === 2}>
                <WelcomeScreen
                    step={step}
                    image={<IconReport className="w-full h-full object-contain" />}
                    title="Responsible Access Controlled By You"
                    description="Easily set limits, customize access, and ensure responsible usage that aligns with your business policies. Your employees gain the benefits, and you retain the oversight."
                    submitText="Get Started"
                    submitAction={() => {setStep(1); setTab("readyToSubmit")}}
                />
            </Maybe>
        </div>
    )
}


const WelcomeScreen = (props) => {
    const {step, image, title, description, submitText, submitAction} = props;
    
    return (
        <div className="mx-auto p-8 mt-7 max-w-xl text-center rounded-3xl bg-white">
            <div className="h-32">
                {image}
            </div>
            <div className="mt-8 font-semibold text-2xl leading-7">
                {title}
            </div>
            <div className="mt-5 text-[#4A5564] leading-6 text-lg tracking-wide">
                {description}
            </div>
            <div className="my-10 flex space-x-1.5 items-center justify-center">
                {[...Array(2).keys()].map((e) => (
                    <React.Fragment key={e}>
                        <Maybe condition={(e+1) === step}>
                            <div className="w-3 h-3 bg-ep-primary-600 rounded-full"></div>
                        </Maybe>
                        <Maybe condition={(e+1) !== step}>
                            <div className="w-2 h-2 bg-[#9CA3AF] rounded-full"></div>
                        </Maybe>
                    </React.Fragment>
                ))}
            </div>
            <div className="">
                <button type="button" onClick={submitAction} className="btn btn-block btn-lg btn-ep-primary">
                    {submitText}
                </button>
            </div>
        </div>
    )
}


export default OnDemandCompliance;